import { ErrorSummary } from "nhsuk-react-components"
import { useContext, useEffect, useLayoutEffect, useMemo } from "react"
import { Heading } from "react-aria-components"
import { useSearchParams } from "react-router-dom"
import { PageBody } from "../../components/pageBody/PageBody"
import { BackButtonContext } from "../../context/BackButtonContext"
import { useNhsAppJs } from "../../hooks/useNhsAppJs"

type ErrorType = "internal" | "consent_required" | "access_denied" | null

export const ErrorPage = (): JSX.Element => {
  const [searchParams] = useSearchParams()

  const { setOnBackPress, setShowBackButton } = useContext(BackButtonContext)
  const { inApp } = useNhsAppJs()

  const msg = (searchParams.get("msg") as ErrorType) ?? null

  useLayoutEffect(() => {
    if (setShowBackButton) {
      if (inApp && setOnBackPress) {
        setOnBackPress("appointments")
        setShowBackButton(true)
      } else {
        setShowBackButton(false)
      }
    } else {
      console.error("no set for back button")
    }
  }, [inApp, setOnBackPress, setShowBackButton])

  const title = useMemo(() => {
    switch (msg) {
      case "internal":
        return "There is a problem with the service"
      case "consent_required":
      case "access_denied":
        return "You cannot continue without sharing your information"
      default:
        return "There is a problem"
    }
  }, [msg])

  const content = useMemo(() => {
    switch (msg) {
      case "internal":
        return (
          <>
            <p>This may be a temporary problem.</p>
            <p>Go back and try logging in again. If you cannot log in, try again later. </p>
            <p>
              For urgent medical advice, go to <a href="https://111.nhs.uk/">111.nhs.uk</a> or call{" "}
              <a href="tel:111">111</a>.
            </p>
          </>
        )

      case "consent_required":
      case "access_denied":
        return (
          <>
            <p>The service provider needs your NHS login information to verify your identity.</p>
            <p>
              If you need medical help, go to <a href="https://111.nhs.uk/">111.nhs.uk</a> or call{" "}
              <a href="tel:111">111</a> or your GP.
            </p>
            <p>
              Call <a href="tel:999">999</a> if it&apos;s a life-threatening emergency.
            </p>
            {!inApp && <p>Close this tab and go back to the NHS App.</p>}
          </>
        )
      default:
        return (
          <>
            <p>There was an issue with NHS login. This may be a temporary problem. </p>
            <p>Go back and try logging in again. If you cannot log in, try again later.</p>
          </>
        )
    }
  }, [inApp, msg])

  useEffect(() => {
    if (title) {
      document.title = title
    }
  }, [title])

  if (["consent_required", "access_denied"].includes(msg ?? "")) {
    return (
      <PageBody allowLogout={false}>
        <div className="error-page-summary">
          <Heading level={1}>{title}</Heading>
          <ErrorSummary.Body>{content}</ErrorSummary.Body>
        </div>
      </PageBody>
    )
  }

  return (
    <PageBody allowLogout={false}>
      <ErrorSummary className="error-page-summary">
        <ErrorSummary.Title>{title}</ErrorSummary.Title>
        <ErrorSummary.Body>{content}</ErrorSummary.Body>
      </ErrorSummary>
    </PageBody>
  )
}
